import { gql, useMutation } from "@apollo/client";
import { Button, Form, Input, InputNumber, message, Modal, Select } from "antd";
import InputNumberWithAddon from "components/input-number-with-addon";
import FormItem from "antd/es/form/FormItem";
import TextArea from "antd/lib/input/TextArea";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import {
  CompanyParticipationType,
  ErrorMessages,
  SidepanelStyles,
  UserParticipationType
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { useProjectParticipants } from "hooks/project-participants";
import { useAutoFocusFirstField } from "hooks/useAutoFocusFirstField";
import { useContext, useRef, useState } from "react";
import { restrictInputToNumbers } from "utils/inpututils";
import { formatNumberWithCommas, validatorForFieldValue } from "utils/utils";

function CreateBidPackageComponent(props: {
  setDrawerOpen: (open: boolean) => void;
  showDrawerOpen: boolean;
  modelTitle: string;
  onBidPackageCreated: any;
}) {
  const [form] = Form.useForm();
  const { setDrawerOpen, showDrawerOpen, modelTitle, onBidPackageCreated } =
    props;
  const [isLoading, setLoading] = useState(false);
  const {
    gqlClientForProject,
    tokenRetrievalState,
    columnHeaders: { bidPackageHeaderMap }
  } = useContext(ProjectContext);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const token = tokenRetrievalState?.token;
  const { projectParticipants } = useProjectParticipants();
  const subContractors = projectParticipants?.materialTradePartners;

  useAutoFocusFirstField(formContainerRef, {
    enabled: showDrawerOpen,
    modal: true
  });

  const [createBidPackage] = useMutation<any>(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const onFinish = async (values: any) => {
    const user_participants: Array<{
      user_id: string;
      user_participation_type_id: UserParticipationType;
    }> = [];

    if (values?.preconstruction_lead) {
      user_participants.push({
        user_id: values.preconstruction_lead,
        user_participation_type_id: UserParticipationType.PRECONSTRUCTION_LEAD
      });
    }
    if (values?.operations_lead) {
      user_participants.push({
        user_id: values.operations_lead,
        user_participation_type_id: UserParticipationType.OPERATIONS_LEAD
      });
    }

    const company_participants = (values?.bidding_subcontractors || []).map(
      (vendorId: string) => ({
        vendor_id: vendorId,
        company_participation_type_id:
          CompanyParticipationType.BIDDING_SUB_CONTRACTOR
      })
    );

    const variables = {
      feature_type: "BID_PACKAGE",
      feature_instances: [
        {
          number: values?.number,
          title: values?.title,
          wave: values?.wave,
          description: values?.description,
          estimated_value: values?.estimated_value,
          no_of_bidders: values?.no_of_bidders,
          user_participants,
          company_participants
        }
      ]
    };

    setLoading(true);

    const creatResponse: any = await createBidPackage({
      variables
    });

    setLoading(false);

    if (creatResponse?.data?.insert_feature_instances_multi?.ids?.length) {
      onBidPackageCreated(
        values?.title,
        creatResponse?.data?.insert_feature_instances_multi?.ids[0]
      );
    } else {
      message.error("Unable to create Bid Package.");
    }
  };

  return (
    <Modal
      className="custom-drawer with-body-scroll"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div
          className="flex justify-end space-x-2 items-center"
          data-testid="index-modal"
        >
          <Button
            form="CreateBidPacakgeForm"
            disabled={isLoading}
            onClick={() => {
              setDrawerOpen(false);
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            form="CreateBidPacakgeForm"
            loading={isLoading}
            disabled={isLoading}
            htmlType="submit"
            type="primary"
            data-testid="index-button-create"
          >
            Create Bid Package
          </Button>
        </div>
      }
      open={showDrawerOpen}
      onCancel={() => {
        setDrawerOpen(false);
      }}
      destroyOnHidden
    >
      <div ref={formContainerRef}>
        <Form
          preserve={false}
          form={form}
          id="CreateBidPacakgeForm"
          scrollToFirstError
          layout="vertical"
          onFinish={onFinish}
        >
          <FormItem
            name="number"
            label="Number"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.bidPackgeNumber
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "number",
                    value?.trim(),
                    "BID_PACKAGE",
                    token,
                    ErrorMessages.BidPackageNumberMsg
                  );
                }
              }
            ]}
          >
            <Input data-autofocus="true" />
          </FormItem>
          <FormItem
            name="title"
            label="Bid Package Title"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.bidPackgeTitle
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "title",
                    value?.trim(),
                    "BID_PACKAGE",
                    token,
                    ErrorMessages.BidPackageTitleMsg
                  );
                }
              }
            ]}
          >
            <Input />
          </FormItem>
          <FormItem name="description" label="Description ">
            <TextArea />
          </FormItem>
          <FormItem name="wave" label="Wave">
            <Input className="w-full" />
          </FormItem>
          {/* Order matches Bid Package details: Wave → No. of Bidders → Bidding
              Subcontractors → Estimated Value; leads last like the Leads card. */}
          <FormItem
            name="no_of_bidders"
            label={bidPackageHeaderMap?.no_of_bidders || "Number of Bidders"}
          >
            <InputNumber
              className="w-full"
              type="number"
              min={0}
              onKeyDown={restrictInputToNumbers}
            />
          </FormItem>
          <FormItem
            name="bidding_subcontractors"
            label={
              bidPackageHeaderMap?.bidding_subcontractors ||
              "Bidding Subcontractors"
            }
          >
            <Select
              mode="multiple"
              options={
                subContractors
                  ? subContractors.map((company: any) => ({
                      id: company.vendor_id,
                      label: company.subscription_vendor?.name || company.name,
                      value: company.vendor_id
                    }))
                  : []
              }
              removeIcon={null}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </FormItem>
          <FormItem
            name="estimated_value"
            label={bidPackageHeaderMap?.estimated_value || "Estimated Value"}
          >
            <InputNumberWithAddon
              className="w-full"
              rootClassName="w-full"
              addonBefore="$"
              min={0}
              onKeyDown={restrictInputToNumbers}
              formatter={(value: any) => formatNumberWithCommas(value)}
            />
          </FormItem>
          <FormItem
            name="preconstruction_lead"
            label={
              bidPackageHeaderMap?.preconstruction_lead ||
              "Preconstruction Lead"
            }
          >
            <GCReviewersDropdown />
          </FormItem>
          <FormItem
            name="operations_lead"
            label={bidPackageHeaderMap?.operations_lead || "Operations Lead"}
          >
            <GCReviewersDropdown />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
}

export default CreateBidPackageComponent;
